// Gravações reais (CC0) em public/audio/. O manifest.json lista só os arquivos que
// existem, então nada é buscado à toa (sem 404 no console). Sem arquivo, o jogo
// toca o som sintetizado (plano B).

export type SampleGroup = 'diceShake' | 'diceThrow' | 'clash';
export type MusicTrack = 'menu' | 'battle' | 'boss';

export interface AudioManifest {
  /** Formatos disponíveis para todos os arquivos, em ordem de preferência. */
  formats: string[];
  music: Partial<Record<MusicTrack, string>>;
  samples: Partial<Record<SampleGroup, string[]>>;
}

export const EMPTY_MANIFEST: AudioManifest = { formats: [], music: {}, samples: {} };

/** Valida o JSON do manifesto; qualquer coisa estranha vira manifesto vazio. */
export function parseManifest(raw: unknown): AudioManifest {
  if (!raw || typeof raw !== 'object') return EMPTY_MANIFEST;
  const r = raw as Record<string, unknown>;
  const str = (x: unknown): x is string => typeof x === 'string' && /^[\w.-]+$/.test(x);
  const formats = Array.isArray(r.formats) ? r.formats.filter((f): f is string => f === 'ogg' || f === 'mp3') : [];
  const music: AudioManifest['music'] = {};
  const m = (r.music ?? {}) as Record<string, unknown>;
  for (const k of ['menu', 'battle', 'boss'] as const) if (str(m[k])) music[k] = m[k] as string;
  const samples: AudioManifest['samples'] = {};
  const s = (r.samples ?? {}) as Record<string, unknown>;
  for (const k of ['diceShake', 'diceThrow', 'clash'] as const) {
    const list = Array.isArray(s[k]) ? (s[k] as unknown[]).filter(str) : [];
    if (list.length) samples[k] = list;
  }
  if (!formats.length) return EMPTY_MANIFEST;
  return { formats, music, samples };
}

/** Escolhe o formato que o navegador toca (ogg primeiro; Safari antigo fica com mp3). */
export function pickFormat(formats: string[], canPlay: (mime: string) => boolean): string | null {
  const mime: Record<string, string> = { ogg: 'audio/ogg; codecs="vorbis"', mp3: 'audio/mpeg' };
  return formats.find((f) => mime[f] && canPlay(mime[f])) ?? null;
}

export type Fetcher = (url: string) => Promise<{ ok: boolean; json(): Promise<unknown>; arrayBuffer(): Promise<ArrayBuffer> }>;

/**
 * Carrega o manifesto e decodifica as amostras. Erros (rede, arquivo, decodificação)
 * nunca vão para o console: a amostra só fica de fora e o sintetizado toca no lugar.
 */
export class SampleBank {
  manifest: AudioManifest = EMPTY_MANIFEST;
  format: string | null = null;
  private buffers = new Map<SampleGroup, AudioBuffer[]>();
  private loading: Promise<void> | null = null;

  constructor(
    private base: string,
    private fetcher: Fetcher,
    private canPlay: (mime: string) => boolean,
  ) {}

  url(name: string): string | null {
    return this.format ? `${this.base}audio/${name}.${this.format}` : null;
  }

  /** Busca o manifesto (uma vez). */
  load(): Promise<void> {
    this.loading ??= this.fetcher(`${this.base}audio/manifest.json`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((raw) => {
        this.manifest = parseManifest(raw);
        this.format = pickFormat(this.manifest.formats, this.canPlay);
      });
    return this.loading;
  }

  /** Decodifica as amostras do manifesto num AudioContext. */
  async decode(ctx: Pick<BaseAudioContext, 'decodeAudioData'>): Promise<void> {
    await this.load();
    if (!this.format) return;
    for (const [group, names] of Object.entries(this.manifest.samples) as [SampleGroup, string[]][]) {
      const list: AudioBuffer[] = [];
      for (const name of names) {
        try {
          const r = await this.fetcher(this.url(name)!);
          if (!r.ok) continue;
          list.push(await ctx.decodeAudioData(await r.arrayBuffer()));
        } catch {
          /* fica de fora; o sintetizado cobre */
        }
      }
      if (list.length) this.buffers.set(group, list);
    }
  }

  has(group: SampleGroup): boolean {
    return (this.buffers.get(group)?.length ?? 0) > 0;
  }

  /** Uma variação aleatória do grupo. */
  pick(group: SampleGroup, rnd = Math.random): AudioBuffer | null {
    const list = this.buffers.get(group);
    if (!list?.length) return null;
    return list[Math.floor(rnd() * list.length)];
  }

  musicUrl(track: MusicTrack): string | null {
    const name = this.manifest.music[track];
    return name ? this.url(name) : null;
  }
}
